import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
} from 'chart.js';
import { Box, Grid } from '@mui/material';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

interface HorizontalBarChartProps {
  data: any;

  /**
   * 차트 높이
   */
  height?: number | string;

  /**
   * 막대 색상 기본값: 코발트 블루
   */
  color?: string;

  /**
   * 막대 두께
   */
  barThickness?: number;

  /**
   * 막대 모서리 둥글기
   */
  borderRadius?: number;

  /**
   * 값 뒤에 붙일 단위
   * 예: 원, 억원, 건, %, 대
   */
  unit?: string;

  /**
   * 오른쪽 라벨 공간
   */
  rightPadding?: number;

  /**
   * 범례 표시 여부
   */
  showLegend?: boolean;

  /**
   * 막대 옆 데이터 라벨 표시 여부
   */
  showDataLabels?: boolean;

  /**
   * x축 눈금 표시 여부
   */
  showXAxis?: boolean;

  /**
   * x축 grid 표시 여부
   */
  showXGrid?: boolean;

  /**
   * y축 grid 표시 여부
   */
  showYGrid?: boolean;

  /**
   * 데이터 없을 때 문구
   */
  emptyText?: string;

  dataLabelBold?: boolean; // data bold 여부
  yLabelBold?: boolean; // y bold 여부

  dataLabelPosition?: 'right' | 'top' | 'smartTop';
  shortBarRatio?: number;
}

const toNumber = (value: unknown): number => {
  if (Array.isArray(value)) {
    return Number(value[1] ?? value[0] ?? 0);
  }

  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatNumber = (value: number) => {
  return value.toLocaleString();
};

export default function HorizontalBarChart({
  data: chartData,
  height = 280,
  color = '#0047AB',
  barThickness = 16,
  borderRadius = 0,
  unit = '',
  rightPadding = 0,
  showLegend = true,
  showDataLabels = true,
  showXAxis = true,
  showXGrid = false,
  showYGrid = false,
  emptyText = '표시할 데이터가 없습니다.',
  dataLabelBold = false,
  yLabelBold = true,
  dataLabelPosition = 'smartTop',
  shortBarRatio = 0.15,
}: HorizontalBarChartProps) {
  const formatValue = (value: number) => {
    return `${value}${unit}`;
  };
  const isEmpty =
    !chartData ||
    !chartData.labels ||
    chartData.labels.length === 0 ||
    !chartData.datasets ||
    chartData.datasets.length === 0 ||
    !chartData.datasets[0].data ||
    chartData.datasets[0].data.length === 0;

  if (isEmpty) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: height,
        }}
      >
        {emptyText}
      </Box>
    );
  }

  /**
   * 화면에서 넘어온 chartData에 색상/두께 기본값을 보정
   * 각 화면에서 backgroundColor를 지정하면 그 값을 우선 사용
   */
  const resolvedData: ChartData<'bar'> = {
    ...chartData,
    datasets: chartData.datasets.map((dataset) => ({
      ...dataset,
      backgroundColor: dataset.backgroundColor ?? color,
      borderColor: dataset.borderColor ?? color,
      borderRadius: dataset.borderRadius !== undefined ? dataset.borderRadius : borderRadius,
      barThickness: dataset.barThickness !== undefined ? dataset.barThickness : barThickness,
      maxBarThickness:
        dataset.maxBarThickness !== undefined ? dataset.maxBarThickness : barThickness,
    })),
  };

  const getDataLabelAlign = (context) => {
    if (dataLabelPosition === 'right') {
      return 'right';
    }

    if (dataLabelPosition === 'top') {
      return 'top';
    }

    if (dataLabelPosition === 'smartTop') {
      return isShortBar(context) ? 'right' : 'top';
    }

    return 'right';
  };

  const getDataLabelOffset = (context) => {
    if (dataLabelPosition === 'right') {
      return 8;
    }

    if (dataLabelPosition === 'top') {
      return 4;
    }

    if (dataLabelPosition === 'smartTop') {
      return isShortBar(context) ? 8 : 4;
    }

    return 8;
  };

  const getValueRatio = (context): number => {
    const dataset = context.dataset.data ?? [];
    const currentValue = toNumber(dataset[context.dataIndex]);
    const maxValue = Math.max(...dataset.map((value) => toNumber(value)));
    if (!maxValue) {
      return 0;
    }
    return currentValue / maxValue;
  };

  const isShortBar = (context): boolean => {
    return getValueRatio(context) < shortBarRatio;
  };

  const options: ChartOptions<'bar'> = {
    indexAxis: 'y',

    responsive: true,
    maintainAspectRatio: false,

    layout: {
      padding: {
        right: rightPadding,
      },
    },

    plugins: {
      legend: {
        display: showLegend,
      },

      datalabels: {
        display: showDataLabels,
        anchor: 'end',
        align: (context) => {
          return isShortBar(context) ? 'right' : 'top';
        },
        offset: (context) => {
          return getDataLabelOffset(context);
        },
        clip: false,

        font: {
          weight: dataLabelBold ? 'bold' : 'normal',
        },

        formatter: (value) => {
          return formatValue(value);
        },
      },
    },

    scales: {
      x: {
        beginAtZero: true,
        display: showXAxis,
        grid: {
          display: showXGrid,
        },
        ticks: {
          callback: (value) => {
            return formatNumber(toNumber(value));
          },
        },
        grace: '25%',
      },

      y: {
        grid: {
          display: showYGrid,
        },
        ticks: {
          font: {
            weight: yLabelBold ? 'bold' : 'normal',
          },
        },
      },
    },
  };

  return (
    /*<div style={{ width: '100%', height }}>{*/
    <Bar data={resolvedData} options={options} />
    /*}</div>*/
  );
}
